import type React from 'react';

import type { ProposalListItem } from '@/lib/proposals/core/types';
import type { CalendarEvent } from '@/pages/calendar/utils/eventMapper';

import { ActionRow } from './ActionRow';
import { MeetingsNeedingNotesList } from './MeetingsNeedingNotesList';

export interface RecapViewProps {
  todayActions: ProposalListItem[];
  meetingsNeedingNotes: CalendarEvent[];
  orgId: number;
  timeZone?: string;
  /** Undefined when the user lacks the ahoy_note create grant — the meetings
   *  list then renders no "Add summary" button. */
  onAddNote?: (meetingEntityId: string) => void;
  onStartAction?: (proposalId: number) => void;
  onDismissAction: (proposalId: number) => Promise<void>;
  onShowAllActions: () => void;
  selectedIds: Set<number>;
  onToggleSelection: (id: number, shiftKey?: boolean) => void;
  firstActionCheckboxRef?: React.RefObject<HTMLButtonElement | null>;
}

interface RecapViewHeaderProps {
  onShowAllActions: () => void;
}

function RecapViewHeader({ onShowAllActions }: RecapViewHeaderProps) {
  return (
    <div className='flex items-center justify-between mt-0.5 mb-3'>
      <h2 className='text-[15px] font-semibold text-primary'>Day in review</h2>
      <button
        type='button'
        onClick={onShowAllActions}
        className='text-[12px] font-semibold text-primary hover:text-tertiary transition-colors'
      >
        Show all pending actions
      </button>
    </div>
  );
}

export function RecapView({
  todayActions,
  meetingsNeedingNotes,
  orgId,
  timeZone,
  onAddNote,
  onStartAction,
  onDismissAction,
  onShowAllActions,
  selectedIds,
  onToggleSelection,
  firstActionCheckboxRef,
}: RecapViewProps) {
  return (
    <div className='space-y-4'>
      <RecapViewHeader onShowAllActions={onShowAllActions} />
      <MeetingsNeedingNotesList
        meetings={meetingsNeedingNotes}
        onAddNote={onAddNote}
        timeZone={timeZone}
      />
      <div className='space-y-2'>
        <h3 className='text-[13px] font-medium text-[var(--color-text-secondary)]'>
          Today&apos;s actions
        </h3>
        {todayActions.length === 0 ? (
          <p className='text-[13px] text-[var(--color-text-secondary)] py-4 text-center'>
            No new actions today.
          </p>
        ) : (
          <div className='space-y-1.5'>
            {todayActions.map((action, index) => (
              <ActionRow
                key={action.id}
                action={action}
                isSelected={selectedIds.has(action.id)}
                onToggleSelection={onToggleSelection}
                orgId={orgId}
                onStartAction={onStartAction}
                onDismissAction={onDismissAction}
                firstCheckboxRef={
                  index === 0 ? firstActionCheckboxRef : undefined
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
